import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Pencil, Plus } from "lucide-react";

export default function SmsTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [nom, setNom] = useState("");
  const [corps, setCorps] = useState("");
  const [editingId, setEditingId] = useState(null);

  const fetchTemplates = async () => {
    try {
      const res = await api.get("/sms-templates");
      setTemplates(res.data.items);
    } catch (e) {
      toast.error("Erreur lors du chargement des modeles");
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const resetForm = () => {
    setNom("");
    setCorps("");
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/sms-templates/${editingId}`, { nom, corps });
        toast.success("Modele mis a jour");
      } else {
        await api.post("/sms-templates", { nom, corps });
        toast.success("Modele cree");
      }
      resetForm();
      fetchTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setNom(t.nom);
    setCorps(t.corps);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/sms-templates/${id}`);
      toast.success("Modele supprime");
      fetchTemplates();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="sms-templates-page">
      <form onSubmit={handleSubmit} className="border border-border p-6 space-y-4" data-testid="sms-template-form">
        <div className="space-y-2">
          <Label className="font-mono text-xs uppercase tracking-wider">Nom du modele</Label>
          <Input
            data-testid="sms-template-nom-input"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            placeholder="Ex: Relance courte"
            required
            className="rounded-none"
          />
        </div>
        <div className="space-y-2">
          <Label className="font-mono text-xs uppercase tracking-wider">Message</Label>
          <Textarea
            data-testid="sms-template-corps-input"
            value={corps}
            onChange={(e) => setCorps(e.target.value)}
            rows={4}
            placeholder="Ex: Bonjour {nom}, je vous contacte au sujet de..."
            required
            className="rounded-none font-mono text-sm"
          />
          <p className="font-mono text-xs text-muted-foreground">
            Variables disponibles: {"{nom}"} {"{metier}"} {"{nb_avis}"} {"{adresse}"} {"{telephone}"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="submit" data-testid="sms-template-submit-button" className="rounded-none font-mono uppercase tracking-wider gap-2">
            <Plus size={14} /> {editingId ? "Mettre a jour" : "Creer le modele"}
          </Button>
          {editingId && (
            <Button
              type="button"
              variant="outline"
              className="rounded-none font-mono uppercase tracking-wider"
              onClick={resetForm}
              data-testid="sms-template-cancel-edit-button"
            >
              Annuler
            </Button>
          )}
        </div>
      </form>

      <div className="border border-border divide-y divide-border" data-testid="sms-templates-list">
        {templates.map((t) => (
          <div key={t.id} className="p-4 flex items-start justify-between gap-4" data-testid={`sms-template-row-${t.id}`}>
            <div>
              <p className="font-medium">{t.nom}</p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{t.corps}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="icon" className="rounded-none" onClick={() => handleEdit(t)} data-testid={`sms-template-edit-${t.id}`}>
                <Pencil size={14} />
              </Button>
              <Button variant="outline" size="icon" className="rounded-none" onClick={() => handleDelete(t.id)} data-testid={`sms-template-delete-${t.id}`}>
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <p className="p-6 text-center text-muted-foreground font-mono text-xs uppercase" data-testid="sms-templates-empty-state">
            Aucun modele
          </p>
        )}
      </div>
    </div>
  );
}
